export class AuthError extends Error {
  constructor() {
    super("Not authenticated");
    this.name = "AuthError";
  }
}

export class RequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null = null,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "RequestError";
  }
}

export class ReauthenticationRequired extends RequestError {
  constructor(message: string) {
    super(message, 403, "reauthentication_required");
    this.name = "ReauthenticationRequired";
  }
}

export const API_URL = __ROADBRO_API_URL__;

type ErrorBody = { error?: { code?: string; message?: string; details?: Record<string, unknown> } };

function isErrorBody(value: unknown): value is ErrorBody {
  return typeof value === "object" && value !== null && "error" in value;
}

type Listener = () => void;
let reauthenticate: (() => Promise<boolean>) | null = null;
const unauthenticatedListeners = new Set<Listener>();

export function setReauthenticationHandler(handler: (() => Promise<boolean>) | null) {
  reauthenticate = handler;
}

export function onUnauthenticated(listener: Listener): () => void {
  unauthenticatedListeners.add(listener);
  return () => {
    unauthenticatedListeners.delete(listener);
  };
}

async function send<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("X-RoadBro-Admin", "1");
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { cache: "no-store", credentials: "include", ...init, headers });
  } catch {
    throw new RequestError("network", 0, "network_error");
  }
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      payload = text;
    }
  }
  if (response.ok) {
    return payload as T;
  }
  const error = isErrorBody(payload) ? payload.error : undefined;
  if (response.status === 401) {
    throw new AuthError();
  }
  if (error?.code === "reauthentication_required") {
    throw new ReauthenticationRequired(error.message ?? "reauthentication_required");
  }
  throw new RequestError(error?.message ?? `Request failed with status ${response.status}`, response.status, error?.code ?? null, error?.details ?? {});
}

async function withSessionHandling<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof AuthError) {
      unauthenticatedListeners.forEach((listener) => listener());
      throw error;
    }
    if (error instanceof ReauthenticationRequired && reauthenticate && (await reauthenticate())) {
      return run();
    }
    throw error;
  }
}

export function adminFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return withSessionHandling(() => send<T>(path, init));
}

export function adminGet<T>(path: string): Promise<T> {
  return adminFetch<T>(path);
}

export function adminSend<T>(method: "POST" | "PUT" | "PATCH" | "DELETE", path: string, body?: unknown): Promise<T> {
  return adminFetch<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
}

export function rawSend<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  return send<T>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });
}

export function query(params: Record<string, string | number | boolean | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== "") {
      search.set(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}
